import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, UserRound, ShieldCheck, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/lib/upload";
import { isLightHex } from "@/lib/theme";
import { useCustomer } from "@/lib/customer";
import { CustomerLoginForm, RulesConsent } from "@/pages/CustomerLogin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const needsAccept = (d, c) => {
  const has = d.rules_text || d.rules_file_id || d.privacy_text || d.privacy_file_id;
  if (!has) return !c.name;
  const at = c.rules_accepted?.[d.id];
  const v = c.rules_accepted_version?.[d.id];
  return !c.name || !at || Number(v || 1) !== Number(d.rules_version || 1);
};

function CompleteProfile({ d, customer, onDone }) {
  const [form, setForm] = useState({ name: customer.name || "", phone: customer.phone || "", marketing_consent: Boolean(customer.marketing_consent) });
  const [rules, setRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasRules = d.rules_text || d.rules_file_id || d.privacy_text || d.privacy_file_id;
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il tuo nome");
    if (hasRules && !rules) return toast.error("Accetta il regolamento e l'informativa privacy");
    setBusy(true);
    try {
      onDone(await api.customerAcceptContest(d.id, { ...form, rules_accepted: true }));
      toast.success("Registrazione al concorso completata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="contest-complete-profile">
      <p className="text-sm flex items-center gap-2"><UserRound className="h-4 w-4" /> Ciao <b>{customer.email}</b>: completa l'iscrizione a <b>{d.name}</b></p>
      <Input data-testid="contest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome *" />
      <Input data-testid="contest-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefono WhatsApp (facoltativo)" />
      <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="contest-marketing-checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" /><span>Acconsento a ricevere comunicazioni promozionali (facoltativo)</span></label>
      {hasRules && <RulesConsent contest={d} checked={rules} onChange={setRules} testid="contest-rules-checkbox" />}
      <Button data-testid="contest-complete-submit" onClick={submit} disabled={busy} className="w-full h-11 gap-2"><ShieldCheck className="h-4 w-4" /> {busy ? "Salvo…" : "Conferma e partecipa"}</Button>
    </div>
  );
}

export default function ContestLogin({ hostMode = false }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { customer, setCustomer } = useCustomer();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const back = hostMode ? "/" : `/c/${slug}`;

  useEffect(() => {
    api.landingResolve(hostMode ? "" : slug, hostMode ? window.location.hostname : "").then(setD).catch((e) => setErr(e?.response?.data?.detail || "Pagina non trovata"));
  }, [slug, hostMode]);
  useEffect(() => {
    if (d && customer && !needsAccept(d, customer)) navigate(back, { replace: true });
  }, [d, customer, back, navigate]);

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-950 text-white p-6 text-center" data-testid="contest-login-error"><p className="text-2xl font-bold">{err}</p></div>;
  if (!d || customer === undefined) return <div className="min-h-screen bg-slate-950" />;
  const rawBg = (d.game?.bg_color || "").toLowerCase();
  const bg = !rawBg || rawBg === "#0f172a" ? "#ffffff" : rawBg;
  const accent = d.game?.accent_color || "#2f6fe4";
  const logo = fileUrl(d.logo_file_id) || d.logo_url;
  const light = isLightHex(bg);

  return (
    <div className={`min-h-screen flex flex-col ${light ? "lp-light text-slate-900" : "lp-dark text-white"}`} style={{ "--lp-bg": bg, "--lp-accent": accent, background: bg }} data-testid="contest-login-page">
      <div className="max-w-md w-full mx-auto px-5 py-8 flex-1 flex flex-col">
        <button type="button" onClick={() => navigate(back)} className="inline-flex items-center gap-1 text-sm opacity-75 hover:opacity-100 self-start" data-testid="contest-login-back"><ArrowLeft className="h-4 w-4" /> Torna al concorso</button>
        <div className="mt-6 text-center">
          {logo ? <img src={logo} alt={d.name} className="h-20 mx-auto object-contain" data-testid="contest-login-logo" /> : <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center font-black text-xl" style={{ background: accent, color: "#fff" }}>{d.name.slice(0, 1)}</div>}
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl mt-4" data-testid="contest-login-title">{d.name}</h1>
          <p className="text-sm opacity-75 mt-1">{customer ? "Completa la tua iscrizione al concorso" : "Registrati o accedi per partecipare"}</p>
          {d.landing?.signup_reward_enabled && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" data-testid="contest-login-reward">
              <Gift className="h-3.5 w-3.5" /> Registrandoti ricevi: {d.landing.signup_reward_description || (d.landing.signup_reward_type === "bonus_play" ? "una giocata bonus" : "uno sconto di benvenuto")}
            </p>
          )}
        </div>
        <div className="mt-8 rounded-3xl bg-white text-slate-900 shadow-2xl p-6 border border-slate-200" data-testid="contest-login-card">
          {customer ? <CompleteProfile d={d} customer={customer} onDone={setCustomer} /> : <CustomerLoginForm next={window.location.pathname} contest={d} />}
        </div>
        <p className="mt-6 text-center text-xs opacity-60">
          <a href={`/regolamento/${d.id}`} className="underline" data-testid="contest-login-rules">Regolamento</a> · <a href={`/privacy/${d.id}`} className="underline" data-testid="contest-login-privacy">Privacy</a>
        </p>
      </div>
    </div>
  );
}
